import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiExternalLink, FiX, FiCheck, FiCopy, FiSmartphone, FiAlertCircle } = FiIcons;

export default function QRCodeModal({ isOpen, onClose, bookmark, item, themeColors = {}, isDark = true }) {
  const [copied, setCopied] = useState(false);
  const [imgError, setImgError] = useState(false);
  const [useFallbackCdn, setUseFallbackCdn] = useState(false);

  const bm = bookmark || item;
  if (!isOpen || !bm || !bm.url) return null;

  const primaryQr = `https://api.qrserver.com/v1/create-qr-code/?size=260x260&margin=12&data=${encodeURIComponent(bm.url)}`;
  const fallbackQr = `https://quickchart.io/qr?size=260&margin=2&text=${encodeURIComponent(bm.url)}`;
  const qrSrc = useFallbackCdn ? fallbackQr : primaryQr;

  const handleCopy = () => {
    try {
      navigator.clipboard.writeText(bm.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      console.warn('Clipboard write error:', e);
    }
  };

  const handleImgError = () => {
    if (!useFallbackCdn) {
      setUseFallbackCdn(true);
    } else {
      setImgError(true);
    }
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 bg-black/75 backdrop-blur-md"
        />

        <motion.div
          initial={{ opacity: 0, scale: 0.94, y: 15 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.94, y: 15 }}
          transition={{ duration: 0.18 }}
          className="relative w-full max-w-sm rounded-2xl bg-[#141418] border border-[#26262e] shadow-2xl p-6 text-center z-10"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2 text-xs font-semibold text-[#888]">
              <SafeIcon icon={FiSmartphone} className="w-4 h-4 text-emerald-400" />
              <span>Scan to Open on Mobile</span>
            </div>
            <button
              onClick={onClose}
              className="p-1 rounded-lg text-[#666] hover:text-white hover:bg-[#222] transition-colors"
              aria-label="Close"
            >
              <SafeIcon icon={FiX} className="w-4 h-4" />
            </button>
          </div>

          <h3 className="text-base font-bold text-white mb-1 truncate px-2">
            {bm.title || 'Bookmark'}
          </h3>
          <p className="text-xs text-[#888] truncate mb-5 px-2 font-mono">
            {bm.url}
          </p>

          {/* QR Code Container */}
          <div className="bg-white p-3 rounded-2xl w-[220px] h-[220px] mx-auto shadow-xl flex items-center justify-center mb-4 overflow-hidden relative">
            {!imgError ? (
              <img
                src={qrSrc}
                alt={`QR Code for ${bm.title}`}
                className="w-full h-full object-contain rounded-lg"
                onError={handleImgError}
              />
            ) : (
              <div className="text-center p-4 text-neutral-800">
                <SafeIcon icon={FiAlertCircle} className="w-8 h-8 text-neutral-400 mx-auto mb-2" />
                <p className="text-xs font-semibold">QR Code Blocked</p>
                <p className="text-[10px] text-neutral-500 mt-1">
                  Adblocker or offline. Use the copy button below.
                </p>
              </div>
            )}
          </div>

          {/* Instructions */}
          <p className="text-[11px] text-[#777] mb-4">
            Point your smartphone camera at the code to open this website instantly.
          </p>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleCopy}
              className="flex-1 py-2.5 rounded-xl text-xs font-medium text-[#ccc] hover:text-white bg-[#1c1c22] hover:bg-[#262630] border border-[#282832] transition-colors flex items-center justify-center gap-2"
            >
              <SafeIcon icon={copied ? FiCheck : FiCopy} className="w-3.5 h-3.5 text-emerald-400" />
              <span>{copied ? 'Link Copied!' : 'Copy Link'}</span>
            </button>
            <a
              href={bm.url}
              target="_blank"
              rel="noopener noreferrer"
              className="px-3.5 py-2.5 rounded-xl text-xs font-medium text-[#ccc] hover:text-white bg-[#1c1c22] hover:bg-[#262630] border border-[#282832] transition-colors flex items-center justify-center"
              title="Open in new tab"
            >
              <SafeIcon icon={FiExternalLink} className="w-3.5 h-3.5" />
            </a>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
